import { useNavigate } from "react-router-dom";
import { useNotifications } from "@/contexts/Notifications";
import { useTranslations } from "@/contexts/Translation";
import { H1 } from "@/components/Headers/Headings";
import Section from "@/components/Section";
import Button from "@/components/Button/Button";
import { CommentIcon, Ended, Survey } from "@/components/Icons";
import type { Notification, NotificationType } from "@/types";
import styles from "./Notifications.module.css";

const typeIcon: Record<NotificationType, typeof Survey> = {
  response: Survey,
  survey_comment: CommentIcon,
  goal_comment: CommentIcon,
  survey_ended: Ended,
  goal_ended: Ended,
};

const resourcePath = (n: Notification): string => {
  if (n.resourceType === "survey") return `/survey/${n.resourceId}`;
  return `/improvement/${n.resourceId}`;
};

const NotificationItem = ({ notification }: { notification: Notification }) => {
  const t = useTranslations("notifications");
  const { markAsRead } = useNotifications();
  const navigate = useNavigate();
  const Icon = typeIcon[notification.type];

  const handleClick = async () => {
    if (!notification.isRead) {
      await markAsRead(notification.id);
    }
    navigate(resourcePath(notification));
  };

  return (
    <li data-testid='notification-item' className={[styles.item, notification.isRead ? styles.itemRead : styles.itemUnread].filter(Boolean).join(" ")}>
      <button type='button' className={styles.itemButton} onClick={handleClick} aria-label={t(`type_${notification.type}`)}>
        <span className={styles.iconWrap} aria-hidden='true' data-testid={`notification-icon-${notification.type}`}>
          {Icon && <Icon width='22px' height='22px' />}
        </span>
        <span className={styles.itemContent}>
          <span className={styles.itemType}>{t(`type_${notification.type}`)}</span>
          <span className={styles.itemDate}>{new Date(notification.registered).toLocaleString()}</span>
        </span>
        {!notification.isRead && <span className={styles.unreadDot} aria-hidden='true' />}
      </button>
    </li>
  );
};

const Notifications = () => {
  const t = useTranslations("notifications");
  const { notifications, loading, unreadCount, markAllAsRead } = useNotifications();

  return (
    <Section>
      <div className={styles.header}>
        <H1 data-testid='notifications-page-heading'>{t("heading")}</H1>
        {unreadCount > 0 && (
          <Button type='button' onClick={markAllAsRead} testId='mark-all-read'>
            {t("markAllRead")}
          </Button>
        )}
      </div>

      {loading && (
        <div data-testid='notifications-loading' aria-live='polite' aria-atomic='true' aria-busy='true'>
          <p>{t("loading")}</p>
        </div>
      )}

      {!loading && notifications.length === 0 && <p data-testid='notifications-empty' className={styles.empty}>{t("empty")}</p>}

      {notifications.length > 0 && (
        <ul data-testid='notifications-list' className={styles.list} aria-label={t("heading")}>
          {notifications.map((n) => (
            <NotificationItem key={n.id} notification={n} />
          ))}
        </ul>
      )}
    </Section>
  );
};

export default Notifications;
